import { User, Cars, HotelOffers } from 'types';
import i18n from '../plugins/i18n';
import { get as getCookie } from 'es-cookie';
import { parse, differenceInDays } from 'date-fns';

export namespace OfferUseCase {
  export function mapFinalPrices(
    price: HotelOffers.Price,
    splitModel: User.Split,
  ) {
    const prices = [];
    if (!price && !splitModel) return prices;
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(
          String(i18n.global.t('offers.finalPrices.totalAtHotel')),
        ),
        new HotelOffers.Label(price.savePercent.formatCurrency()),
      ),
    );
    if (price.save > 0) {
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.finalPrices.discount')),
          ),
          new HotelOffers.Label(price.save.formatCurrency()),
        ),
      );
    } else if (price.cashback > 0) {
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.finalPrices.cashback')),
          ),
          new HotelOffers.Label(price.cashback.formatCurrency()),
        ),
      );
    }
    if (splitModel.planModel && splitModel.getPlanPriceFormatted()) {
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(splitModel.selectedPlanLabel()),
          new HotelOffers.Label(splitModel.getPlanPriceFormatted()),
        ),
      );
    }
    if (splitModel.getOrderFee() > 0) {
      const feeContent =
        splitModel.getOrderFee() * 1.2 > price.save
          ? String(i18n.global.t('offers.finalPrices.feeMoreSavings'))
          : String(i18n.global.t('offers.finalPrices.feeLessSavings'));
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.finalPrices.fee')),
          ),
          new HotelOffers.Label(splitModel.getOrderFee().formatCurrency()),
          false,
          feeContent,
        ),
      );
    }
    return prices;
  }

  export function mapCarFinalPrices(priceDetails: Cars.PriceDetails) {
    const prices = [];
    // if (!price && !splitModel) return prices;
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label('Total due at counter'),
        new HotelOffers.Label(
          priceDetails.source.remainder.amount.formatCurrency({
            currency: priceDetails.source.remainder.currency,
            decimals: 2,
          }),
        ),
      ),
    );
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label('Cash back after your ride'),
        new HotelOffers.Label(priceDetails.cashback.formatCurrency()),
      ),
    );

    return prices;
  }

  export function getFinalPayNowPrice(
    price: HotelOffers.Price,
    splitModel: User.Split,
  ) {
    const bookingFee = splitModel.getOrderFee();
    const planPrice = splitModel.getOriginalPlanPrice();
    const total = price.effectivePrice + bookingFee + planPrice;
    return String(
      i18n.global.t('offers.finalPayNowPrice', {
        total: total.formatCurrency(),
      }),
    );
  }

  export function facilities(offer: HotelOffers.Offer) {
    const facilities = [];
    facilities.push(offer.bed);
    facilities.push(
      offer.flags.includes(HotelOffers.FilterKeys.PayNow)
        ? i18n.global.t('offers.facilities.prepay')
        : i18n.global.t('offers.facilities.payAfter'),
    );
    facilities.push(
      offer.flags.includes(HotelOffers.FilterKeys.FreeCancellation)
        ? i18n.global.t('filters.freeCancellation')
        : i18n.global.t('filters.nonRefundable'),
    );
    if (offer.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded))
      facilities.push('n/a'); //todo
    // if (offer.view) facilities.push(offer.view);
    return facilities;
  }

  export function saving(offer: HotelOffers.Offer): string {
    if (offer.price.price) {
      return 'Best Deal';
    } else {
      const prtv = getCookie('prtv');
      const cashbackText =
        prtv && prtv === '1'
          ? String(
              i18n.global.t('offers.savings.getBackText', {
                saving: offer.price.save.formatCurrency(),
              }),
            )
          : String(
              i18n.global.t('offers.savings.afterCashback', {
                saving: offer.price.cashback.formatCurrency(),
              }),
            );
      return offer.price.save > 0
        ? cashbackText
        : String(i18n.global.t('offers.savings.bestDeal'));
    }
  }

  // export function paymentDescription(offer: HotelOffers.OfferV2): string {
  //   return offer.payment_description.replaceAll('\\*{2}(.+?)\\*{2}', '<b>\$1</b>');
  // }

  export function getTotalPrices(
    offer: HotelOffers.Offer,
    adults: number,
    nights: number,
    useCredits: boolean,
  ) {
    const purple = '#5e06b6';
    const grey = 'rgba(0, 0, 0, 0.54)';
    const prices = [];
    const credits =
      offer.priceWithCredits && useCredits
        ? offer.priceWithCredits
        : offer.priceWithCredits;

    const supplements = offer.price.taxes;

    const guests = i18n.global.t('offers.totalPrices.guests', adults);
    const nightsText = i18n.global.t('offers.totalPrices.nights', nights);
    const room = i18n.global.t('offers.totalPrices.room', nights);

    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(room, false, grey),
        new HotelOffers.Label(credits.price.formatCurrency(), false, grey),
      ),
    );
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(
          String(i18n.global.t('offers.totalPrices.taxesAndFees')),
          false,
          grey,
        ),
        new HotelOffers.Label(credits.taxes.formatCurrency(), false, grey),
        false,
        'taxes',
      ),
    );
    if (credits.save > 0)
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.totalPrices.discount')),
            false,
            grey,
          ),
          new HotelOffers.Label(
            '-' + credits.save.formatCurrency(),
            false,
            grey,
          ),
        ),
      );
    prices.push(new HotelOffers.ConfirmLabel(null, null, true));
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(
          String(i18n.global.t('offers.totalPrices.totalAtHotel')),
        ),
        new HotelOffers.Label(credits.base.formatCurrency()),
      ),
    );
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(
          String(i18n.global.t('offers.totalPrices.totalNow')),
        ),
        new HotelOffers.Label(credits.price.formatCurrency()),
      ),
    );
    if (credits.cashback > 0)
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.totalPrices.cashback')),
            true,
            purple,
          ),
          new HotelOffers.Label(
            credits.cashback.formatCurrency(),
            true,
            purple,
          ),
        ),
      );
    prices.push(new HotelOffers.ConfirmLabel(null, null, true));
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(
          String(i18n.global.t('offers.totalPrices.tripTotal')),
          true,
        ),
        new HotelOffers.Label(credits.effectivePrice.formatCurrency(), true),
      ),
    );
    return prices;
  }

  export function getTotalPricesV2(
    offer: HotelOffers.Offer,
    adults: number,
    nights: number,
    useCredits: boolean,
  ) {
    const purple = '#5e06b6';
    const prices = [];
    const credits =
      offer.priceWithCredits && useCredits
        ? offer.priceWithCredits
        : offer.price;

    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(offer.originalName, false),
        new HotelOffers.Label(credits.price.formatCurrency(), false),
      ),
    );
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label('Taxes', false),
        new HotelOffers.Label(credits.taxes.formatCurrency(), false),
      ),
    );
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label('Cashback', true, purple),
        new HotelOffers.Label(credits.cashback.formatCurrency(), true, purple),
      ),
    );
    prices.push(new HotelOffers.ConfirmLabel(null, null, true));
    return prices;
    // const purple = '#5e06b6';
    // const grey = 'rgba(0, 0, 0, 0.54)';
    // const prices = [];
    // const credits = offer.prices.enable_credits && useCredits ? offer.prices.enable_credits : offer.prices.disable_credits;
    // const supplements = offer.prices.supplements;
    // const guests = i18n.tc('offers.totalPrices.guests', adults);
    // const nightsText = i18n.tc('offers.totalPrices.nights', nights);
    // const room = i18n.tc('offers.totalPrices.room', nights);
    // prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(room, false, grey), new HotelOffers.Label(credits.public_price.formatCurrency(), false, grey)));
    // prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(String(i18n.t('offers.totalPrices.taxesAndFees')), false, grey), new HotelOffers.Label(credits.supplements_and_taxes.formatCurrency(), false, grey), false, 'taxes'));
    // if (credits.discount > 0) prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(String(i18n.t('offers.totalPrices.discount')), false, grey), new HotelOffers.Label('-'+credits.discount.formatCurrency(), false, grey)));
    // prices.push(new HotelOffers.ConfirmLabel(null, null, true));
    // prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(String(i18n.t('offers.totalPrices.totalAtHotel'))), new HotelOffers.Label(credits.pay_at_HotelOffers.formatCurrency())));
    // prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(String(i18n.t('offers.totalPrices.totalNow'))), new HotelOffers.Label(credits.pay_now.formatCurrency())));
    // if (credits.cashback > 0) prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(String(i18n.t('offers.totalPrices.cashback')), true, purple), new HotelOffers.Label(credits.cashback.formatCurrency(), true, purple)));
    // prices.push(new HotelOffers.ConfirmLabel(null, null, true));
    // prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(String(i18n.t('offers.totalPrices.tripTotal')), true), new HotelOffers.Label(credits.trip_total.formatCurrency(), true)));
    // return prices;
  }

  export function getTripTotalPrice(
    offer: HotelOffers.Offer,
    useCredits: boolean,
  ) {
    const credits =
      offer.priceWithCredits && useCredits
        ? offer.priceWithCredits
        : offer.price;

    return new HotelOffers.ConfirmLabel(
      new HotelOffers.Label(
        String(i18n.global.t('offers.totalPrices.tripTotal')),
        true,
      ),
      new HotelOffers.Label(credits.effectivePrice.formatCurrency(), true),
    );
  }

  export function getPriceBreakDown(
    offer: HotelOffers.Offer,
    adults: number,
    nights: number,
    useCredits: boolean,
  ) {
    const room = i18n.global.t('offers.priceBreakDown.room', adults);
    const prices = [];
    const supplements = offer.price.taxes;

    const credits =
      offer.priceWithCredits && useCredits
        ? offer.priceWithCredits
        : offer.price;

    prices.push(averageRateText(credits));
    prices.push(new HotelOffers.ConfirmLabel(new HotelOffers.Label(room)));
    prices.push(averageRateText2(nights, credits));
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(
          String(i18n.global.t('offers.priceBreakDown.taxesPerNight')),
        ),
        new HotelOffers.Label(credits.taxes.formatCurrency()),
      ),
    );

    if (credits.save > 0) {
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.priceBreakDown.roomSubtotal')),
            true,
          ),
          new HotelOffers.Label(credits.effectivePrice.formatCurrency(), true),
        ),
      );
    }

    // if (supplements && supplements.length > 0) extraTaxes(supplements).map(it => prices.push(it));

    if (credits.cashback > 0) {
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.priceBreakDown.discount')),
            true,
          ),
          new HotelOffers.Label((credits.cashback * -1).formatCurrency(), true),
        ),
      );
    }

    if (credits.save > 0) {
      prices.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            String(i18n.global.t('offers.priceBreakDown.redeemCredits')),
            true,
          ),
          new HotelOffers.Label((credits.save * -1).formatCurrency(), true),
        ),
      );
    }

    prices.push(new HotelOffers.ConfirmLabel(null, null, true));
    prices.push(
      new HotelOffers.ConfirmLabel(
        new HotelOffers.Label(
          String(i18n.global.t('offers.priceBreakDown.tripTotal')),
          true,
        ),
        new HotelOffers.Label(credits.effectivePrice.formatCurrency(), true),
      ),
    );

    return prices;
  }

  export function averageRateText(
    price: HotelOffers.Price,
    roundToDollar: boolean = false,
  ) {
    // const value = price.market_price
    //   ? price.public_price_per_night
    //   : price.price_with_extra_savings_per_night;

    const value = price.effectivePrice;

    const purple = '#5e06b6';
    return new HotelOffers.ConfirmLabel(
      new HotelOffers.Label(
        String(i18n.global.t('offers.averageRateText')),
        true,
        purple,
      ),
      new HotelOffers.Label(
        roundToDollar ? value.formatCurrency() : value.formatCurrency(),
        true,
        purple,
      ),
    );
  }

  export function averageRateText2(nights: number, price: HotelOffers.Price) {
    const label = String(i18n.global.t('offers.publicLowestRate', nights));
    return new HotelOffers.ConfirmLabel(
      new HotelOffers.Label(label),
      new HotelOffers.Label(price.price.formatCurrency()),
    );
  }

  export function cancellationV2(
    cancellationPolicies: HotelOffers.CancellationPolicies[],
    currency: string,
    refundable: boolean,
    partialRefundable: boolean,
    isUserLocalTime: boolean,
    cancellation_info: string = '',
    supplier: string,
    pricePer: number,
    checkin?: Date,
    freeCancellationUntil?: string,
  ): HotelOffers.ConfirmLabel[] {
    const labels: HotelOffers.ConfirmLabel[] = [];

    const reddish = 'var(--red-color)';
    const aqua = '#00dcb4';
    const lang = i18n.global.locale;
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };

    const afterMask = (date: Date) => {
      const dd = date.toLocaleString(lang, options);
      return String(
        i18n.global.t('offers.cancellation.afterMask', { mask: dd }),
      );
    };

    const beforeMask = (date: Date) => {
      const dd = date.toLocaleString(lang, options);
      return String(
        i18n.global.t('offers.cancellation.beforeMask', { mask: dd }),
      );
    };

    const getZone = (date: string) => {
      if (date.endsWith('Z')) {
        return {
          zone: 0,
          display: '+00:00',
        };
      }

      return {
        zone: parseInt(date.slice(-6).substring(0, 3)),
        display: date.slice(-6),
      };
    };

    const currentDate = Date();

    if (
      (refundable || partialRefundable) &&
      cancellationPolicies &&
      cancellationPolicies.length > 0
    ) {
      let freeUntilPolicy = cancellationPolicies[0];
      if (
        cancellationPolicies[0].price === 0 &&
        cancellationPolicies.length > 1
      ) {
        freeUntilPolicy = cancellationPolicies[1];
      }

      if (freeCancellationUntil) {
        freeUntilPolicy = {
          price: 0,
          currency,
          from: freeCancellationUntil,
        };
      }

      const date = parse(freeUntilPolicy.from);
      const zone = getZone(freeUntilPolicy.from);
      const formattedDate = parse(freeUntilPolicy.from).zone(zone.zone);
      const dateByPredict = isUserLocalTime ? date : formattedDate;

      if (differenceInDays(date, currentDate) > 0) {
        labels.push(
          new HotelOffers.ConfirmLabel(
            new HotelOffers.Label(
              `${String(i18n.global.t('offers.cancellation.free'))} `,
              false,
              aqua,
            ),
            new HotelOffers.Label(
              ' — ' +
                beforeMask(dateByPredict) +
                (isUserLocalTime ? '' : ' (' + zone.display + ')'),
              false,
            ),
          ),
        );
      }

      cancellationPolicies.forEach((policy, index) => {
        if (policy.price && policy.from) {
          const from = policy.from ? parse(policy.from) : undefined;
          const price = policy.price.formatCurrency({
            currency,
            decimals: 2,
          });

          const zone = getZone(policy.from);
          const formattedDate = parse(policy.from).zone(zone.zone);
          const dateByPredict = isUserLocalTime ? from : formattedDate;
          labels.push(
            new HotelOffers.ConfirmLabel(
              new HotelOffers.Label(price + ' '),
              new HotelOffers.Label(
                ' — ' +
                  afterMask(dateByPredict) +
                  (isUserLocalTime ? '' : ' (' + zone.display + ')'),
              ),
            ),
          );
        }
      });
    } else if (
      refundable &&
      cancellationPolicies.length == 0 &&
      supplier.toLowerCase() === 'travelport' &&
      checkin
    ) {
      var dayBeforeCheckin = new Date();
      dayBeforeCheckin.setTime(checkin.getTime() - 24 * 60 * 60 * 1000);
      labels.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            `${String(i18n.global.t('offers.cancellation.free'))} `,
            false,
            aqua,
          ),
          new HotelOffers.Label(' — ' + beforeMask(dayBeforeCheckin), false),
        ),
      );
      labels.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            pricePer.formatCurrency({
              currency: currency,
              decimals: 2,
            }) + ' ',
          ),
          new HotelOffers.Label(' — ' + afterMask(dayBeforeCheckin)),
        ),
      );
    } else {
      labels.push(
        new HotelOffers.ConfirmLabel(
          new HotelOffers.Label(
            `${String(i18n.global.t('filters.nonRefundable'))} `,
            false,
            reddish,
          ),
          new HotelOffers.Label(
            ' — ' +
              (cancellation_info
                ? cancellation_info
                : String(i18n.global.t('offers.cancellation.nonRefundable'))),
          ),
        ),
      );
    }

    return labels;
  }

  export function offerFormattedForAnalytics(offer: HotelOffers.Offer) {
    if (!offer) {
      return null;
    }
    const prices = offer.price;

    const savings = prices.save - prices.cashback;

    return {
      pubprice: prices?.price?.roundCent() ?? '',
      price: prices?.strikethrough?.roundCent() ?? '',
      cashback: prices?.cashback?.roundCent() ?? '',
      savings: savings?.roundCent() ?? '',
      savings_percent: Math.round((savings / prices.strikethrough) * 100),
    };
  }

  export function composeUniqueOfferKey(offer: HotelOffers.Offer): string {
    let key = offer.category ?? '';

    const optionsKey = offer.flags?.join() ?? '';

    key += optionsKey;

    const provider = offer.price?.provider ?? '';

    key += provider;

    key += offer.bed ?? '';

    key += offer.originalName ?? '';

    return key;
  }

  export const getProviderColor = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'booking':
        return '#006CE4';
            
      case 'expedia':
      case 'privateexpedia':
        return '#FDDB32';

      case 'itravex':
        return '#30903A';

      default:
        return '#000';
    }
  }
}
